<template>
    <nav class="work-pdf-thumbnails" aria-label="Miniature delle pagine">
        <div v-for="(number, index) in sequence" :key="number" class="work-pdf-thumbnails__item"
            @dragover.prevent @drop.prevent="drop(number)" @dragend="dragged = null">
            <button :ref="`page${number}`" type="button" class="work-pdf-thumbnail"
                :data-page="number" :aria-label="`Vai a pagina ${index + 1}`" :aria-current="page === number ? 'page' : null"
                :draggable="editable && !disabled" :class="{ selected: page === number }" @dragstart="startDrag(number, $event)" @click="$emit('page', number)">
                <span class="work-pdf-thumbnail__page">
                    <canvas :ref="`canvas${number}`" aria-hidden="true" />
                    <svg v-if="previews[number] && previews[number].marks.length" class="work-pdf-thumbnail__markup" aria-hidden="true" :viewBox="`0 0 ${previews[number].width} ${previews[number].height}`">
                        <work-pdf-annotation-shapes :marks="previews[number].marks" :marker-id="`pdf-thumb-arrow-${_uid}-${number}`" :factor="previews[number].factor" :stroke-scale="previews[number].factor" />
                    </svg>
                </span>
                <span>{{ index + 1 }}</span>
            </button>
            <div v-if="editable && sequence.length > 1" class="work-pdf-thumbnails__moves">
                <button type="button" :disabled="disabled || index === 0" :aria-label="`Sposta pagina ${index + 1} prima`" @click="$emit('move', { from: index, to: index - 1 })">↑</button>
                <button type="button" :disabled="disabled || index === sequence.length - 1" :aria-label="`Sposta pagina ${index + 1} dopo`" @click="$emit('move', { from: index, to: index + 1 })">↓</button>
            </div>
        </div>
    </nav>
</template>

<script>
import { readPdfPageRotation } from "./pdfOrientation.mjs";
import { initialPageOrder } from "./pdfPageOrder.mjs";
import { projectPdfMarks } from "./pdfMarkupProjection.mjs";

export default {
    name: "WorkPdfThumbnails",
    components: { WorkPdfAnnotationShapes: () => import(/* webpackChunkName: "work-pdf-editor" */ "./WorkPdfAnnotationShapes.vue") },
    props: { source: { type: Object, required: true }, page: Number, order: Array, editable: Boolean, disabled: Boolean, annotations: { type: Array, default: () => [] } },
    data: () => ({ dragged: null, viewports: {} }),
    computed: {
        sequence() { return this.order?.length ? this.order : initialPageOrder(this.source.pdf.numPages); },
        previews() {
            const result = {};
            for (const number of this.sequence) {
                const viewport = this.viewports[number];
                if (viewport) result[number] = { width: Math.ceil(viewport.width), height: Math.ceil(viewport.height), factor: viewport.scale,
                    marks: projectPdfMarks(this.annotations, viewport, number) };
            }
            return result;
        },
    },
    mounted() {
        this._queue = []; this._seen = new Set(); this._abort = false;
        this._observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                const number = Number(entry.target.dataset.page);
                if (entry.isIntersecting && !this._seen.has(number)) { this._seen.add(number); this._queue.push(number); }
            }
            this.renderNext();
        }, { root: this.$el, rootMargin: "100px" });
        this.$el.querySelectorAll("[data-page]").forEach((node) => this._observer.observe(node));
    },
    beforeDestroy() {
        this._abort = true; this._observer?.disconnect(); this._thumbnailRenderTask?.cancel();
        this.$el.querySelectorAll("canvas").forEach((canvas) => { canvas.width = 0; canvas.height = 0; });
    },
    watch: { page(value) { this.$refs[`page${value}`]?.[0]?.scrollIntoView({ block: "nearest" }); } },
    methods: {
        startDrag(number, event) {
            if (!this.editable || this.disabled) { event.preventDefault(); return; }
            this.dragged = number;
            event.dataTransfer.effectAllowed = "move";
            event.dataTransfer.setData("text/plain", String(number));
        },
        drop(number) {
            if (this.editable && !this.disabled && this.dragged !== null) {
                const from = this.sequence.indexOf(this.dragged), to = this.sequence.indexOf(number);
                if (from >= 0 && to >= 0 && from !== to) this.$emit("move", { from, to });
            }
            this.dragged = null;
        },
        async renderNext() {
            if (this._busy || this._abort) return;
            this._busy = true;
            while (this._queue.length && !this._abort) {
                const number = this._queue.shift();
                try {
                    const page = await this.source.pdf.getPage(number);
                    const rotation = (page.rotate + await readPdfPageRotation(page)) % 360;
                    if (this._abort) break;
                    const original = page.getViewport({ scale: 1, rotation });
                    const viewport = page.getViewport({ scale: Math.min(96 / original.width, 126 / original.height), rotation });
                    this.$set(this.viewports, number, Object.freeze(viewport));
                    const canvas = this.$refs[`canvas${number}`][0];
                    canvas.width = Math.ceil(viewport.width); canvas.height = Math.ceil(viewport.height);
                    this._thumbnailRenderTask = page.render({ canvasContext: canvas.getContext("2d"), viewport });
                    await this._thumbnailRenderTask.promise;
                } catch { /* A failed thumbnail must never hide the full drawing. */ }
            }
            this._busy = false;
        },
    },
};
</script>

<style scoped>
.work-pdf-thumbnails { position: absolute; inset: 0 auto 0 0; width: 116px; overflow: auto; padding: 8px; background: var(--pdf-surface); border-right: 1px solid var(--pdf-border); }
.work-pdf-thumbnails__item { margin-bottom: 8px; }
.work-pdf-thumbnail { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; width: 100%; min-height: 100px; padding: 6px 0; border: 2px solid transparent; color: inherit; border-radius: 4px; }
.work-pdf-thumbnail[draggable="true"] { cursor: grab; }
.work-pdf-thumbnails__moves { display: flex; }
.work-pdf-thumbnails__moves button { flex: 1; min-height: 32px; color: inherit; border: 1px solid var(--pdf-border); }
.work-pdf-thumbnails button:disabled { opacity: .35; }
.work-pdf-thumbnails button.selected { border-color: #70ced7; background: #34474e; }
.work-pdf-thumbnails button:focus-visible { outline: 2px solid #70ced7; }
.work-pdf-thumbnails canvas { max-width: 100%; height: auto; background: white; }
.work-pdf-thumbnail__page { display: inline-block; position: relative; max-width: 100%; line-height: 0; }
.work-pdf-thumbnail__page canvas { display: block; }
.work-pdf-thumbnail__markup { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; overflow: hidden; }
.work-pdf-thumbnails span { font-size: 12px; }
@media (max-width: 600px) {
    .work-pdf-thumbnails { width: 64px; padding: 4px; }
    .work-pdf-thumbnail { min-height: 70px; }
    .work-pdf-thumbnails__moves { flex-direction: column; }
    .work-pdf-thumbnails__moves button { min-height: 44px; }
}
</style>
